import React, { useEffect, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import Pagination from '@cloudscape-design/components/pagination';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import api from '../services/apiService';
import { formatDateDe } from '../utils/date';

const requestStatusOptions = [
  { value: 'all', label: 'Alle Status' },
  { value: 'pending_verification', label: 'E-Mail-Bestätigung ausstehend' },
  { value: 'distribution_processing', label: 'Verteilung läuft' },
  { value: 'distribution_failed', label: 'Verteilung fehlgeschlagen' },
  { value: 'open', label: 'Offen' },
  { value: 'partially_fulfilled', label: 'Teilweise erfüllt' },
  { value: 'fulfilled', label: 'Erfüllt' },
  { value: 'unmatched', label: 'Ohne passenden Vermieter' },
];

const statusLabels = {
  pending_verification: 'E-Mail-Bestätigung ausstehend',
  distribution_processing: 'Verteilung läuft',
  distribution_failed: 'Verteilung fehlgeschlagen',
  open: 'Offen',
  unmatched: 'Ohne passenden Vermieter',
  partially_fulfilled: 'Teilweise erfüllt',
  fulfilled: 'Erfüllt',
  invited: 'Eingeladen',
  viewed: 'Angesehen',
  offered: 'Preisinformation abgegeben',
  selected: 'Ausgewählt',
  not_selected: 'Nicht ausgewählt',
  expired: 'Abgelaufen',
  submitted: 'Abgegeben',
  in_progress: 'In Bearbeitung',
  offer_sent: 'Angebot versendet',
  pending: 'Ausstehend',
  sent: 'Versendet',
  failed: 'Fehlgeschlagen',
  verified: 'Bestätigt',
  legacy_verified: 'Altdaten – als bestätigt übernommen',
};

const requestStatusTypes = {
  pending_verification: 'pending',
  distribution_processing: 'in-progress',
  distribution_failed: 'error',
  open: 'pending',
  partially_fulfilled: 'in-progress',
  fulfilled: 'success',
  unmatched: 'warning',
};

function labelStatus(value) {
  return statusLabels[value] || value || '–';
}

function formatDateTime(value) {
  if (!value) return '–';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '–'
    : new Intl.DateTimeFormat('de-DE', {
      dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Berlin',
    }).format(date);
}

function formatMoney(value, currency = 'EUR') {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '–';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(amount);
}

function machineLabel(item) {
  if (item.machine) return `${item.machine.manufacturer} ${item.machine.model}`;
  return item.category?.name || 'Maschinenkategorie';
}

function actorLabel(event) {
  if (event.actor?.kind === 'requester') {
    return [event.actor.name, event.actor.company, event.actor.email].filter(Boolean).join(' · ');
  }
  if (event.actor?.kind === 'tenant_user') return event.actor.email || 'Vermieter-Benutzer nicht rekonstruierbar';
  if (event.actor?.kind === 'tenant') return event.actor.name || event.actor.email || 'Vermieter';
  return event.tenant?.name || 'System';
}

function timelineDescription(event) {
  switch (event.type) {
    case 'request_created': return 'Mietanfrage gestartet';
    case 'request_verification_email_sent': return 'Bestätigungs-E-Mail an den Anfrager versendet';
    case 'request_verification_email_failed': return 'Versand der Bestätigungs-E-Mail fehlgeschlagen';
    case 'request_email_verified': return 'Anfrager hat seine E-Mail-Adresse bestätigt';
    case 'request_distribution_completed': return 'Verteilung der bestätigten Anfrage abgeschlossen';
    case 'invitation_assigned': return `Anfrage ${event.tenant?.name || 'einem Vermieter'} zugeordnet`;
    case 'invitation_sent': return `An ${event.tenant?.name || 'Vermieter'} weitergeleitet`;
    case 'invitation_delivery_failed': return `Weiterleitung an ${event.tenant?.name || 'Vermieter'} fehlgeschlagen`;
    case 'invitation_viewed': return `${event.tenant?.name || 'Vermieter'} hat die Anfrage angesehen`;
    case 'offer_submitted': return `Preisinformation über ${formatMoney(event.priceNet, event.currency)} abgegeben`;
    case 'offer_selected_and_customer_data_completed': return `${event.tenant?.name || 'Vermieter'} ausgewählt und Firmendaten vervollständigt`;
    case 'offer_status_changed': return `Status von „${labelStatus(event.previousStatus)}“ auf „${labelStatus(event.newStatus)}“ geändert`;
    default: return event.type;
  }
}

function DetailFacts({ entries }) {
  return (
    <div className="admin-request-facts">
      {entries.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value || '–'}</strong>
        </div>
      ))}
    </div>
  );
}

function RequestDetails({ data, loading, error }) {
  if (loading) return <div className="admin-request-loading">Anfragedetails werden geladen …</div>;
  if (error) return <Alert type="error">{error}</Alert>;
  if (!data) return null;
  const { request, invitations, offers, timeline } = data;

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">Anfrager und Anfrage</Header>}>
        <SpaceBetween size="m">
          <DetailFacts entries={[
            ['Gestartet am', formatDateTime(request.createdAt)],
            ['Name', request.requesterName],
            ['Unternehmen', request.company],
            ['E-Mail', request.email],
            ['Telefon', request.phone],
            ['Einsatzort', [request.streetAddress, `${request.postalCode} ${request.city}`, request.country].filter(Boolean).join(', ')],
            ['Mietzeitraum', `${formatDateDe(request.startDate)} – ${formatDateDe(request.endDate)}`],
            ['Miettage', String(request.rentalDays)],
            ['Transport', request.transportMode === 'delivery_pickup' ? 'Anlieferung und Abholung' : 'Selbstabholung'],
            ['Datenschutz akzeptiert', formatDateTime(request.privacyAcceptedAt)],
            ['Bestätigungs-E-Mail', labelStatus(request.emailVerificationStatus)],
            ['Bestätigungs-E-Mail versendet', formatDateTime(request.emailVerificationSentAt)],
            ['E-Mail bestätigt', formatDateTime(request.emailVerifiedAt)],
            ['Verteilung gestartet', formatDateTime(request.distributionStartedAt)],
            ['Verteilung abgeschlossen', formatDateTime(request.distributionCompletedAt)],
          ]} />
          {(request.emailVerificationError || request.distributionError) && (
            <Alert type="error">{request.emailVerificationError || request.distributionError}</Alert>
          )}
          {request.message && <div className="admin-request-message"><span>Nachricht</span><p>{request.message}</p></div>}
        </SpaceBetween>
      </Container>

      <Table
        items={request.items || []}
        wrapLines
        header={<Header variant="h2" counter={`(${request.items?.length || 0})`}>Anfragepositionen</Header>}
        empty="Keine Positionen vorhanden."
        columnDefinitions={[
          { id: 'machine', header: 'Maschine / Kategorie', cell: machineLabel },
          { id: 'quantity', header: 'Anzahl', cell: (item) => item.quantity },
          { id: 'details', header: 'Details', cell: (item) => item.details || '–' },
          {
            id: 'training',
            header: 'Bedienerschulung',
            cell: (item) => item.operatorTrainingRequested
              ? (item.operatorTrainingGroups || []).map((group) => `${group.participantCount} × ${group.language}`).join(', ') || 'Ja'
              : 'Nein',
          },
        ]}
      />

      <Table
        items={invitations}
        wrapLines
        header={<Header variant="h2" counter={`(${invitations.length})`}>Weiterleitung an Vermieter</Header>}
        empty="Die Anfrage wurde keinem Vermieter zugeordnet."
        columnDefinitions={[
          { id: 'tenant', header: 'Vermieter', cell: (invitation) => invitation.tenant?.name || '–' },
          { id: 'station', header: 'Mietstation', cell: (invitation) => invitation.rentalStation ? `${invitation.rentalStation.name} · ${invitation.rentalStation.postalCode} ${invitation.rentalStation.city}` : '–' },
          { id: 'assigned', header: 'Zugeordnet', cell: (invitation) => formatDateTime(invitation.createdAt) },
          { id: 'sent', header: 'Weitergeleitet', cell: (invitation) => formatDateTime(invitation.sentAt) },
          { id: 'viewed', header: 'Angesehen', cell: (invitation) => formatDateTime(invitation.viewedAt) },
          {
            id: 'delivery',
            header: 'E-Mail',
            cell: (invitation) => (
              <>
                <StatusIndicator type={invitation.emailStatus === 'sent' ? 'success' : invitation.emailStatus === 'failed' ? 'error' : 'pending'}>
                  {labelStatus(invitation.emailStatus)}
                </StatusIndicator>
                {invitation.emailError && <small className="admin-request-subline">{invitation.emailError}</small>}
              </>
            ),
          },
          { id: 'status', header: 'Vorgang', cell: (invitation) => labelStatus(invitation.status) },
        ]}
      />

      <Container header={<Header variant="h2" counter={`(${offers.length})`}>Preisinformationen und Auswahl</Header>}>
        {offers.length === 0 ? (
          <div className="admin-request-empty">Noch keine Preisinformation abgegeben.</div>
        ) : (
          <SpaceBetween size="l">
            {offers.map((offer) => (
              <section className="admin-offer-card" key={offer.id}>
                <Header variant="h3" description={`Abgegeben am ${formatDateTime(offer.submittedAt)}`}>
                  {offer.tenant?.name || 'Vermieter'} · {formatMoney(offer.priceNet, offer.currency)} netto
                </Header>
                <DetailFacts entries={[
                  ['Preisbindung', `${offer.priceBindingHours} Stunden`],
                  ['Status', labelStatus(offer.status)],
                  ['E-Mail an Anfrager', labelStatus(offer.requesterEmailStatus)],
                  ['Bestätigung an Vermieter', labelStatus(offer.tenantContactEmailStatus)],
                ]} />
                <Table
                  items={offer.lineItems || []}
                  wrapLines
                  header={<Header variant="h3">Preisdetails</Header>}
                  empty="Keine Preispositionen vorhanden."
                  columnDefinitions={[
                    { id: 'description', header: 'Position', cell: (line) => line.description },
                    { id: 'period', header: 'Zeitraum', cell: (line) => `${formatDateDe(line.dateFrom)} – ${formatDateDe(line.dateTo)}` },
                    { id: 'quantity', header: 'Menge', cell: (line) => `${line.quantity} · ${line.durationDays} Tage` },
                    {
                      id: 'unit',
                      header: 'Einzel-/Satzwert',
                      cell: (line) => line.rateValue !== null && line.rateValue !== undefined
                        ? `${line.rateValue}${line.pricingMode === 'percent_of_rent' ? ' %' : ' €'} · ${line.unitLabel}`
                        : `${formatMoney(line.unitPriceNet)} · ${line.unitLabel}`,
                    },
                    { id: 'total', header: 'Netto gesamt', cell: (line) => formatMoney(line.totalNet) },
                  ]}
                />
                {offer.message && <div className="admin-request-message"><span>Nachricht des Vermieters</span><p>{offer.message}</p></div>}

                {offer.qualifiedAt && (
                  <div className="admin-qualified-data">
                    <Header variant="h3">Ausgewählt am {formatDateTime(offer.qualifiedAt)}</Header>
                    <DetailFacts entries={[
                      ['Ausgewählter Vermieter', offer.tenant?.name],
                      ['Aktueller Vermieterstatus', labelStatus(offer.qualifiedOfferStatus)],
                      ['Firma', offer.qualifiedCompanyName],
                      ['Ansprechpartner', offer.qualifiedContactName],
                      ['E-Mail', offer.qualifiedEmail],
                      ['Telefon', offer.qualifiedPhone],
                      ['Vervollständigte Anschrift', [offer.qualifiedStreetAddress, `${offer.qualifiedPostalCode} ${offer.qualifiedCity}`, offer.qualifiedCountry].filter(Boolean).join(', ')],
                    ]} />
                  </div>
                )}

                <Table
                  items={offer.statusHistory || []}
                  wrapLines
                  header={<Header variant="h3" counter={`(${offer.statusHistory?.length || 0})`}>Vermieter-Statusänderungen</Header>}
                  empty="Keine Statusänderung protokolliert."
                  columnDefinitions={[
                    { id: 'time', header: 'Zeitpunkt', cell: (change) => formatDateTime(change.changedAt) },
                    { id: 'user', header: 'Geändert durch', cell: (change) => change.changedByEmail || change.changedBy?.email || 'Altdaten – Benutzer unbekannt' },
                    { id: 'before', header: 'Vorher', cell: (change) => labelStatus(change.previousStatus) },
                    { id: 'after', header: 'Nachher', cell: (change) => labelStatus(change.newStatus) },
                  ]}
                />
              </section>
            ))}
          </SpaceBetween>
        )}
      </Container>

      <Table
        items={timeline.slice().reverse()}
        wrapLines
        header={<Header variant="h2">Chronologie</Header>}
        empty="Keine Ereignisse vorhanden."
        columnDefinitions={[
          { id: 'time', header: 'Zeitpunkt', cell: (event) => formatDateTime(event.occurredAt) },
          { id: 'event', header: 'Ereignis', cell: timelineDescription },
          { id: 'actor', header: 'Wer', cell: actorLabel },
          { id: 'station', header: 'Mietstation', cell: (event) => event.rentalStation?.name || '–' },
        ]}
      />
    </SpaceBetween>
  );
}

export default function AdminRequestsPage() {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalItems: 0, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(requestStatusOptions[0]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api.get('/platform/requests', {
      params: { page, pageSize: 25, search: search || undefined, status: status.value === 'all' ? undefined : status.value },
    }).then((response) => {
      if (!active) return;
      setItems(response.data.items);
      setPagination(response.data.pagination);
    }).catch((requestError) => {
      if (active) setError(requestError.response?.data?.error || 'Anfragen konnten nicht geladen werden.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [page, search, status]);

  function applySearch() {
    setPage(1);
    setSearch(searchDraft.trim());
  }

  async function openDetails(item) {
    setSelectedRequest(item);
    setDetails(null);
    setDetailsError('');
    setDetailsLoading(true);
    try {
      const response = await api.get(`/platform/requests/${item.id}`);
      setDetails(response.data);
    } catch (requestError) {
      setDetailsError(requestError.response?.data?.error || 'Anfragedetails konnten nicht geladen werden.');
    } finally {
      setDetailsLoading(false);
    }
  }

  return (
    <SpaceBetween size="l">
      <Header variant="h1" description="Anfrager, Weiterleitungen, Preisinformationen, Auswahl und Vermieter-Status lückenlos einsehen">
        Anfragen und Aktivitäten
      </Header>
      {error && <Alert type="error" dismissible onDismiss={() => setError('')}>{error}</Alert>}

      <div className="admin-request-filters">
        <Input
          value={searchDraft}
          placeholder="Referenz, Name, Firma, E-Mail, Ort oder PLZ"
          onChange={({ detail }) => setSearchDraft(detail.value)}
          onKeyDown={({ detail }) => { if (detail.key === 'Enter') applySearch(); }}
        />
        <Select
          selectedOption={status}
          options={requestStatusOptions}
          onChange={({ detail }) => { setStatus(detail.selectedOption); setPage(1); }}
        />
        <Button iconName="search" onClick={applySearch}>Suchen</Button>
      </div>

      <div className="admin-request-table-width">
        <Table
          loading={loading}
          items={items}
          wrapLines
          header={<Header variant="h2" counter={`(${pagination.totalItems})`}>Mietanfragen</Header>}
          empty="Keine passenden Anfragen gefunden."
          columnDefinitions={[
          {
            id: 'request',
            header: 'Anfrage',
            cell: (item) => <><strong>{item.reference}</strong><small className="admin-request-subline">{formatDateTime(item.createdAt)}</small></>,
          },
          {
            id: 'company',
            header: 'Firma',
            cell: (item) => <strong>{item.company || '–'}</strong>,
          },
          {
            id: 'requester',
            header: 'Ansprechpartner',
            cell: (item) => item.requesterName,
          },
          {
            id: 'contact',
            header: 'Kontaktdaten',
            cell: (item) => (
              <>
                <a href={`mailto:${item.email}`}>{item.email}</a>
                <small className="admin-request-subline">{item.phone || 'Keine Telefonnummer'}</small>
              </>
            ),
          },
          { id: 'location', header: 'Einsatzort', cell: (item) => `${item.postalCode} ${item.city}` },
          { id: 'period', header: 'Mietzeitraum', cell: (item) => `${formatDateDe(item.startDate)} – ${formatDateDe(item.endDate)}` },
          { id: 'offers', header: 'Preisinformationen', cell: (item) => `${item.offerCount} / ${item.desiredOfferCount}` },
          {
            id: 'selection',
            header: 'Auswahl',
            cell: (item) => item.selectedOffer
              ? <>{item.selectedOffer.tenantName}<small className="admin-request-subline">{formatDateTime(item.selectedOffer.qualifiedAt)}</small></>
              : '–',
          },
          {
            id: 'status',
            header: 'Status',
            cell: (item) => <StatusIndicator type={requestStatusTypes[item.status] || 'info'}>{labelStatus(item.status)}</StatusIndicator>,
          },
          {
            id: 'action',
            header: 'Aktion',
            cell: (item) => (
              <Button
                variant="inline-icon"
                ariaLabel={`Details zu Anfrage ${item.reference} anzeigen`}
                iconSvg={(
                  <svg viewBox="0 0 16 16" focusable="false">
                    <path d="M1 8s2.5-4 7-4 7 4 7 4-2.5 4-7 4-7-4-7-4Z" />
                    <circle cx="8" cy="8" r="2" />
                  </svg>
                )}
                onClick={() => openDetails(item)}
              />
            ),
          },
          ]}
          pagination={(
            <Pagination
              currentPageIndex={pagination.page}
              pagesCount={pagination.totalPages}
              onChange={({ detail }) => setPage(detail.currentPageIndex)}
            />
          )}
        />
      </div>

      <Modal
        visible={Boolean(selectedRequest)}
        size="max"
        onDismiss={() => { setSelectedRequest(null); setDetails(null); }}
        header={selectedRequest ? `Anfrage ${selectedRequest.reference}` : 'Anfragedetails'}
        footer={<Button onClick={() => { setSelectedRequest(null); setDetails(null); }}>Schließen</Button>}
      >
        <RequestDetails data={details} loading={detailsLoading} error={detailsError} />
      </Modal>
    </SpaceBetween>
  );
}
